/**
 * O texto com formatação da ficha.
 *
 * **Guarda-se texto, não HTML.** Um editor que grava HTML no prontuário obriga
 * a limpar a marcação toda vez que alguém a mostra — e o dia em que a limpeza
 * falhar, o que entra é código executando na tela de quem lê ficha de paciente.
 * Aqui o valor gravado é `**assim**`, legível no banco, na exportação e no PDF,
 * e a tela monta elementos React a partir dele. Não existe caminho por onde
 * marcação vire nó do DOM.
 *
 * A gramática é curta de propósito: negrito, itálico, lista com marcador e
 * lista numerada. É o que uma conduta precisa para destacar dose e ordenar
 * passos. Título, tabela e link viram outros blocos — e a tabela já é um.
 */

export type TrechoDeTexto =
    | { tipo: 'texto'; texto: string }
    | { tipo: 'negrito'; texto: string }
    | { tipo: 'italico'; texto: string };

export type BlocoDeTexto =
    | { tipo: 'paragrafo'; trechos: TrechoDeTexto[] }
    | { tipo: 'lista'; ordenada: boolean; itens: TrechoDeTexto[][] };

/** Marcas que o editor sabe aplicar, e a única coisa que o leitor interpreta. */
export const MARCAS = {
    negrito: { abre: '**', fecha: '**', rotulo: 'Negrito', icone: 'bold', atalho: 'Ctrl+B' },
    italico: { abre: '_', fecha: '_', rotulo: 'Itálico', icone: 'italic', atalho: 'Ctrl+I' },
} as const;

export type NomeDaMarca = keyof typeof MARCAS;

const PADRAO = /(\*\*[^*]+\*\*|_[^_]+_)/g;

/** Quebra uma linha em trechos com e sem marca. */
export function trechosDaLinha(linha: string): TrechoDeTexto[] {
    const partes = linha.split(PADRAO).filter((p) => p !== '');
    return partes.map((parte): TrechoDeTexto => {
        if (parte.startsWith('**') && parte.endsWith('**') && parte.length > 4) {
            return { tipo: 'negrito', texto: parte.slice(2, -2) };
        }
        if (parte.startsWith('_') && parte.endsWith('_') && parte.length > 2) {
            return { tipo: 'italico', texto: parte.slice(1, -1) };
        }
        return { tipo: 'texto', texto: parte };
    });
}

/**
 * O texto em blocos, na ordem.
 *
 * Linhas seguidas viram um parágrafo só (com quebra dentro); linhas de lista
 * seguidas viram uma lista só. É o que a pessoa desenhou ao digitar, e quebrar
 * cada linha num parágrafo próprio afastaria as linhas na leitura.
 */
export function lerTextoRico(bruto: string): BlocoDeTexto[] {
    const blocos: BlocoDeTexto[] = [];
    let paragrafo: string[] = [];
    let lista: { ordenada: boolean; itens: string[] } | null = null;

    const fecharParagrafo = () => {
        if (!paragrafo.length) return;
        blocos.push({ tipo: 'paragrafo', trechos: trechosDaLinha(paragrafo.join('\n')) });
        paragrafo = [];
    };
    const fecharLista = () => {
        if (!lista) return;
        blocos.push({ tipo: 'lista', ordenada: lista.ordenada, itens: lista.itens.map(trechosDaLinha) });
        lista = null;
    };

    for (const linha of String(bruto ?? '').split('\n')) {
        const marcador = /^\s*[-*]\s+(.*)$/.exec(linha);
        const numerada = /^\s*\d+[.)]\s+(.*)$/.exec(linha);

        if (marcador || numerada) {
            fecharParagrafo();
            const ordenada = Boolean(numerada);
            const texto = (marcador?.[1] ?? numerada?.[1] ?? '').trim();
            if (lista && lista.ordenada !== ordenada) fecharLista();
            if (!lista) lista = { ordenada, itens: [] };
            lista.itens.push(texto);
            continue;
        }

        fecharLista();
        if (linha.trim() === '') fecharParagrafo();
        else paragrafo.push(linha);
    }

    fecharLista();
    fecharParagrafo();
    return blocos;
}

/** O texto sem marca nenhuma — para busca, resumo e a linha do tempo. */
export function textoLimpo(bruto: string): string {
    return lerTextoRico(bruto)
        .flatMap((b) => (b.tipo === 'paragrafo' ? [b.trechos] : b.itens))
        .map((trechos) => trechos.map((t) => t.texto).join(''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export interface SelecaoNoTexto {
    inicio: number;
    fim: number;
}

/**
 * Aplica ou remove uma marca no trecho selecionado.
 *
 * Remover importa tanto quanto aplicar: sem isso, clicar duas vezes em negrito
 * daria `****assim****`, que não é negrito de nada. Sem seleção, insere o par de
 * marcas e devolve o cursor no meio, onde a pessoa vai digitar.
 */
export function aplicarMarca(
    texto: string, selecao: SelecaoNoTexto, marca: NomeDaMarca,
): { texto: string; selecao: SelecaoNoTexto } {
    const { abre, fecha } = MARCAS[marca];
    const inicio = Math.min(selecao.inicio, selecao.fim);
    const fim = Math.max(selecao.inicio, selecao.fim);
    const escolhido = texto.slice(inicio, fim);

    if (escolhido.startsWith(abre) && escolhido.endsWith(fecha) && escolhido.length > abre.length + fecha.length) {
        const semMarca = escolhido.slice(abre.length, escolhido.length - fecha.length);
        return {
            texto: texto.slice(0, inicio) + semMarca + texto.slice(fim),
            selecao: { inicio, fim: inicio + semMarca.length },
        };
    }

    const marcado = `${abre}${escolhido}${fecha}`;
    return {
        texto: texto.slice(0, inicio) + marcado + texto.slice(fim),
        selecao: escolhido
            ? { inicio, fim: inicio + marcado.length }
            : { inicio: inicio + abre.length, fim: inicio + abre.length },
    };
}

/**
 * Liga ou desliga o marcador de lista nas linhas tocadas pela seleção.
 *
 * Por LINHA, e não pelo trecho: lista é propriedade da linha inteira, e marcar
 * meia linha produziria um item que começa no meio de uma frase.
 */
export function alternarLista(
    texto: string, selecao: SelecaoNoTexto, ordenada: boolean,
): { texto: string; selecao: SelecaoNoTexto } {
    const inicio = Math.min(selecao.inicio, selecao.fim);
    const fim = Math.max(selecao.inicio, selecao.fim);

    const antes = texto.slice(0, inicio);
    const inicioDaLinha = antes.lastIndexOf('\n') + 1;
    const depois = texto.indexOf('\n', fim);
    const fimDaLinha = depois === -1 ? texto.length : depois;

    const linhas = texto.slice(inicioDaLinha, fimDaLinha).split('\n');
    const jaEhLista = linhas.every((l) => (ordenada ? /^\s*\d+[.)]\s+/ : /^\s*[-*]\s+/).test(l) || l.trim() === '');

    const trocadas = linhas.map((linha, i) => {
        const nu = linha.replace(/^\s*(?:[-*]|\d+[.)])\s+/, '');
        if (jaEhLista) return nu;
        if (nu.trim() === '') return nu;
        return ordenada ? `${i + 1}. ${nu}` : `- ${nu}`;
    });

    const novo = trocadas.join('\n');
    return {
        texto: texto.slice(0, inicioDaLinha) + novo + texto.slice(fimDaLinha),
        selecao: { inicio: inicioDaLinha, fim: inicioDaLinha + novo.length },
    };
}
